import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, ArrowRight, ShoppingBag, UtensilsCrossed, Wrench, Briefcase, Palmtree, Landmark } from "lucide-react";
import { publicApi } from "../lib/api";

// Static routes always shown in the main nav
const STATIC_NAV = [
  { label: "Home", to: "/", end: true },
  { label: "Chi siamo", to: "/chi-siamo" },
];

// Icon + short description per macro-category. Keyed by SLUG first, then falls
// back to matching the human name (case-insensitive) so the header stays in sync
// with the taxonomy even if the CMS renames a category.
const CATEGORY_META = {
  "commercio": { Icon: ShoppingBag, desc: "Negozi, boutique e attività commerciali" },
  "ristorazione": { Icon: UtensilsCrossed, desc: "Bar, pizzerie, ristoranti e locali" },
  "artigianato": { Icon: Wrench, desc: "Artigiani, laboratori e botteghe" },
  "servizi": { Icon: Briefcase, desc: "Professionisti, imprese e studi" },
  "turismo": { Icon: Palmtree, desc: "Strutture ricettive ed esperienze" },
  "enti-associazioni": { Icon: Landmark, desc: "Comune, cultura e associazioni" },
  // Legacy fallbacks (kept in case the CMS is renamed)
  "enti-pubblici-o-privati-e-associazioni": { Icon: Landmark, desc: "Comune, cultura e associazioni" },
  "enti-e-associazioni": { Icon: Landmark, desc: "Comune, cultura e associazioni" },
};
const NAME_META = {
  "commercio": CATEGORY_META["commercio"],
  "ristorazione": CATEGORY_META["ristorazione"],
  "artigianato": CATEGORY_META["artigianato"],
  "servizi": CATEGORY_META["servizi"],
  "turismo": CATEGORY_META["turismo"],
  "enti e associazioni": CATEGORY_META["enti-e-associazioni"],
  "enti pubblici o privati e associazioni": CATEGORY_META["enti-e-associazioni"],
};

const displayName = (raw) =>
  raw === "Enti Pubblici o Privati e Associazioni" ? "Enti e Associazioni" : raw;

const metaFor = (cat) => {
  return (
    CATEGORY_META[cat.slug] ||
    NAME_META[(cat.name || "").toLowerCase()] ||
    { Icon: Briefcase, desc: "Attività associate al CCN" }
  );
};

// ---------------------------------------------------------------------------
// MegaMenu — full-width panel with card grid, fade + slide-down animation.
// ---------------------------------------------------------------------------
const MegaMenu = ({ categories, visible, onClose }) => {
  return (
    <div
      className={`fixed left-0 right-0 top-[72px] z-40 px-4 md:px-8 flex justify-center transition-all duration-[280ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
        visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"
      }`}
      onMouseLeave={onClose}
      data-testid="mega-menu"
    >
      <div className="w-full max-w-[1160px] bg-white border border-[#E5E7EB]/80 rounded-[28px] shadow-[0_30px_60px_-20px_rgba(15,23,42,0.20),0_20px_40px_-15px_rgba(15,23,42,0.10)] p-6 md:p-7">
        <div className="flex items-baseline justify-between px-2 pb-5 mb-5 border-b border-[#F1F5F9]">
          <div>
            <div className="font-display font-semibold text-[15px] text-[#1F2937] tracking-tight">Attività associate</div>
            <div className="text-xs text-slate-500 mt-1">Sei macro categorie, oltre 200 realtà del territorio</div>
          </div>
          <Link
            to="/attivita"
            onClick={onClose}
            className="hidden sm:inline-flex items-center gap-1.5 text-[13px] text-[#12964A] px-2.5 py-1.5 rounded-lg hover:bg-[#F0FAF3] hover:gap-2.5 transition-all duration-300"
            data-testid="mega-see-all"
          >
            Vedi tutte le attività <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {categories.map((c) => {
            const { Icon, desc: fallbackDesc } = metaFor(c);
            // Prefer categories.description from DB, fall back to CATEGORY_META short copy
            const desc = (c.description && c.description.trim()) ? c.description : fallbackDesc;
            return (
              <Link
                key={c.slug}
                to={`/categoria/${c.slug}`}
                onClick={onClose}
                data-testid={`mega-card-${c.slug}`}
                className="group flex items-start gap-3.5 p-[18px] rounded-2xl border border-transparent hover:border-[#12964A]/15 hover:bg-[#FBFDFB] hover:shadow-[0_2px_6px_rgba(15,23,42,0.03),0_12px_28px_rgba(15,23,42,0.06)] hover:-translate-y-0.5 transition-all duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
              >
                <div className="flex-shrink-0 w-11 h-11 rounded-[14px] bg-[#F0FAF3] grid place-items-center text-[#12964A] group-hover:bg-[#E8F5EC] group-hover:scale-[1.06] group-hover:-rotate-[3deg] transition-all duration-300">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-[15.5px] tracking-tight text-[#1F2937] group-hover:text-[#12964A] flex items-center gap-1.5 transition-colors">
                    {displayName(c.name)}
                    <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </div>
                  <div className="text-[13px] text-slate-500 mt-1 leading-snug">{desc}</div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap px-4 py-3.5 bg-[#F0FAF3] rounded-2xl">
          <div className="text-[13px] text-[#0E7C3D]">
            Sei un&apos;attività di Sanluri? <strong className="font-semibold">Unisciti al CCN</strong> e fatti trovare più facilmente.
          </div>
          <Link
            to="/diventa-socio"
            onClick={onClose}
            className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#0E7C3D] px-3.5 py-2 rounded-full bg-white border border-[#12964A]/20 hover:-translate-y-0.5 hover:shadow-[0_6px_14px_rgba(18,150,74,0.15)] transition-all duration-300"
          >
            Diventa socio <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(null);
  const [megaOpen, setMegaOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [subsByCat, setSubsByCat] = useState({});
  const closeTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileCatOpen(null);
    setMegaOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([publicApi.listCategories(), publicApi.listSubcategories()])
      .then(([c, s]) => {
        if (cancelled) return;
        const categories = (c.data || []).filter((x) => x.published !== false);
        setCats(categories);
        const bySlug = {};
        (s.data || []).forEach((sub) => {
          const catSlug = sub.category?.slug;
          if (!catSlug) return;
          if (!bySlug[catSlug]) bySlug[catSlug] = [];
          if (sub.published !== false) bySlug[catSlug].push(sub);
        });
        setSubsByCat(bySlug);
      })
      .catch(() => { /* backend not configured */ });
    return () => { cancelled = true; };
  }, []);

  const macros = useMemo(() => cats.slice(0, 6), [cats]);

  // Mega open/close with small delay to avoid flicker between trigger and panel
  const openMega = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setMegaOpen(true);
  }, []);
  const scheduleClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMegaOpen(false), 140);
  }, []);
  const closeMegaImmediate = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMegaOpen(false);
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeMegaImmediate(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeMegaImmediate]);

  const toggleMobileCat = useCallback((slug) => {
    setMobileCatOpen((cur) => (cur === slug ? null : slug));
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || megaOpen ? "bg-white/85 backdrop-blur-xl border-b border-[#E5E7EB]/70" : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-[72px] gap-6">
            {/* Brand */}
            <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="brand-link">
              <div className="w-10 h-10 rounded-[14px] bg-gradient-to-br from-[#17A855] via-[#12964A] to-[#0E7C3D] flex items-center justify-center text-white font-display text-[17px] font-bold shadow-[0_4px_12px_rgba(18,150,74,0.28)] transition-transform duration-500 group-hover:rotate-[6deg]">C</div>
              <div className="leading-tight hidden sm:block">
                <div className="font-display text-[17px] text-[#1F2937] font-bold tracking-tight">CCN Sanluri</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">Centro Commerciale Naturale</div>
              </div>
            </Link>

            {/* Center nav (desktop only) */}
            <nav className="hidden lg:flex items-center justify-center gap-1 flex-1" aria-label="Navigazione principale">
              {STATIC_NAV.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onMouseEnter={scheduleClose}
                  className={({ isActive }) =>
                    `nav-item relative px-3.5 py-2.5 text-[14.5px] rounded-[10px] transition-colors duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
                      isActive ? "text-[#12964A] nav-item-active" : "text-[#1F2937] hover:text-[#12964A]"
                    }`
                  }
                >
                  {it.label}
                </NavLink>
              ))}

              {/* Attività associate — trigger for mega menu */}
              <button
                type="button"
                onMouseEnter={openMega}
                onMouseLeave={scheduleClose}
                onFocus={openMega}
                onClick={() => setMegaOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={megaOpen}
                data-testid="mega-trigger"
                className={`nav-item relative px-3.5 py-2.5 text-[14.5px] rounded-[10px] inline-flex items-center gap-1 transition-colors duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
                  megaOpen ? "text-[#12964A] nav-item-active" : "text-[#1F2937] hover:text-[#12964A]"
                }`}
              >
                Attività associate
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`} />
              </button>

              <NavLink
                to="/eventi"
                onMouseEnter={scheduleClose}
                className={({ isActive }) =>
                  `nav-item relative px-3.5 py-2.5 text-[14.5px] rounded-[10px] transition-colors duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
                    isActive ? "text-[#12964A] nav-item-active" : "text-[#1F2937] hover:text-[#12964A]"
                  }`
                }
              >
                Eventi
              </NavLink>
              <NavLink
                to="/contatti"
                onMouseEnter={scheduleClose}
                className={({ isActive }) =>
                  `nav-item relative px-3.5 py-2.5 text-[14.5px] rounded-[10px] transition-colors duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
                    isActive ? "text-[#12964A] nav-item-active" : "text-[#1F2937] hover:text-[#12964A]"
                  }`
                }
              >
                Contatti
              </NavLink>
              <NavLink
                to="/diventa-socio"
                onMouseEnter={scheduleClose}
                className={({ isActive }) =>
                  `nav-item relative px-3.5 py-2.5 text-[14.5px] rounded-[10px] transition-colors duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
                    isActive ? "text-[#12964A] nav-item-active" : "text-[#1F2937] hover:text-[#12964A]"
                  }`
                }
              >
                Diventa socio
              </NavLink>
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => navigate("/attivita")}
                aria-label="Cerca attività"
                data-testid="header-search-icon"
                className="hidden md:grid w-10 h-10 rounded-[12px] place-items-center text-[#1F2937] border border-transparent hover:bg-[#F0FAF3] hover:border-[#E8F5EC] hover:text-[#12964A] transition-all duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
              >
                <Search className="w-[18px] h-[18px]" />
              </button>

              <Link
                to="/diventa-socio"
                data-testid="header-join-cta"
                className="hidden md:inline-flex items-center gap-2 px-5 py-[11px] rounded-full bg-[#F4E500] hover:bg-[#F7EA1A] text-[#1B1900] text-[14px] font-semibold shadow-[0_1px_2px_rgba(20,18,0,0.06),0_6px_16px_rgba(244,229,0,0.28)] hover:shadow-[0_4px_8px_rgba(20,18,0,0.08),0_14px_30px_rgba(244,229,0,0.42)] hover:-translate-y-0.5 transition-all duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
              >
                Associati
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>

              <button
                className="lg:hidden p-2 -mr-1 text-[#1F2937] hover:text-[#12964A] transition-colors"
                onClick={() => setMobileOpen((v) => !v)}
                aria-label="Menu"
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mega Menu (desktop) */}
        <div
          onMouseEnter={openMega}
          onMouseLeave={scheduleClose}
          className="hidden lg:block"
        >
          <MegaMenu categories={macros} visible={megaOpen} onClose={closeMegaImmediate} />
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-x-0 top-[72px] z-40 bg-white/95 backdrop-blur-xl border-b border-[#E5E7EB] overflow-hidden transition-[max-height] duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
          mobileOpen ? "max-h-[85vh] overflow-y-auto" : "max-h-0"
        }`}
        data-testid="mobile-drawer"
      >
        <div className="px-6 py-6">
          {STATIC_NAV.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) => `block text-lg font-display py-3 border-b border-slate-200/70 ${isActive ? "text-[#12964A]" : "text-[#1F2937]"}`}
            >
              {it.label}
            </NavLink>
          ))}

          {/* Attività associate accordion (mobile) */}
          <div className="border-b border-slate-200/70">
            <div className="py-3 text-[11px] uppercase tracking-[0.22em] text-slate-400">Attività associate</div>
            {macros.map((c) => {
              const { Icon, desc: fallbackDesc } = metaFor(c);
              const desc = (c.description && c.description.trim()) ? c.description : fallbackDesc;
              const isOpen = mobileCatOpen === c.slug;
              const subs = subsByCat[c.slug] || [];
              return (
                <div key={c.slug} className="border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => toggleMobileCat(c.slug)}
                    className="w-full flex items-center justify-between py-3 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#F0FAF3] grid place-items-center text-[#12964A]">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-[15px] font-display font-semibold text-[#1F2937]">{displayName(c.name)}</div>
                        <div className="text-[11px] text-slate-500">{desc}</div>
                      </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`grid transition-all duration-400 ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <div className="pb-3 pl-12 flex flex-col">
                        <Link to={`/categoria/${c.slug}`} className="py-1.5 text-sm text-[#12964A]">Vedi tutte →</Link>
                        {subs.map((s) => (
                          <Link key={s.id || s.slug} to={`/categoria/${c.slug}/${s.slug}`} className="py-1 text-sm text-[#1F2937] hover:text-[#12964A]">{s.name}</Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <NavLink to="/eventi" className="block text-lg font-display py-3 border-b border-slate-200/70 text-[#1F2937]">Eventi</NavLink>
          <NavLink to="/contatti" className="block text-lg font-display py-3 border-b border-slate-200/70 text-[#1F2937]">Contatti</NavLink>
          <NavLink to="/diventa-socio" className="block text-lg font-display py-3 border-b border-slate-200/70 text-[#12964A]">Diventa socio</NavLink>

          <div className="mt-5 flex flex-col gap-3">
            <Link to="/attivita" className="inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full bg-white border border-[#E5E7EB] text-[#1F2937] text-sm">
              <Search className="w-4 h-4" /> Cerca attività
            </Link>
            <Link to="/diventa-socio" className="inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full bg-[#F4E500] text-[#1B1900] text-sm font-semibold">
              Associati <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
